import {Upload as ImageUpload, Spin} from 'antd';
import {useState, useCallback, useRef} from 'react'; // Import useCallback
import styles from './companyImage.module.scss';
import imgUpload from 'images/upload_image.png';
import {Button} from '@dabafinance/react-components';
import '../companyVideo/player.css';
import {toast} from 'react-toastify';
import {
  resizeImageFile,
  UploadDocumentToBucket,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import _ from 'lodash'; // Import debounce

const UploadImage = ({
  bucket,
  large,
  label = 'Upload Image',
  setPreviewUrl = () => {},
  isACL,
  previewUrl,
  setImgUrl,
  imageProgress,
  thumbnail,
  folder,
  icon,
  /**
   * Limits acceptable file types a user can upload. Defaults to image type.
   */
  accept = 'image/*',
  setProgress = () => {},
  /**
   * Reports whether an upload is in flight, so a parent can block a save that
   * would otherwise submit before the file has a URL. The upload is debounced
   * and resolves well after the preview appears, so the preview is not a signal
   * that the upload is done.
   */
  onUploadingChange = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);

  // The debounced upload keeps the callback from its first render, so it is
  // read through a ref rather than captured.
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const classNameStyle = large
    ? styles['large-image-uploaded']
    : thumbnail
    ? styles['thumbail-upload']
    : icon
    ? styles['icon-upload']
    : styles['image-uploaded'];

  const UploadToBucket = async file => {
    const payload = await resizeImageFile(file);
    try {
      const fileUrl = await UploadDocumentToBucket(
        payload,
        setProgress,
        bucket,
        isACL,
        folder,
      );
      setUploadedImageUrl(fileUrl);
      setImgUrl(fileUrl);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      onUploadingChangeRef.current(false);
    }
  };

  // Debounced version of UploadToBucket
  const debouncedUpload = useCallback(
    _.debounce(file => {
      UploadToBucket(file);
    }, 500),
    [],
  );

  const handleChange = async ({file}) => {
    const temp = file.originFileObj;
    const url = URL.createObjectURL(file.originFileObj);
    setPreviewUrl(url);
    setLoading(true);
    onUploadingChangeRef.current(true);
    debouncedUpload(temp); // Call the debounced function
    setLoading(false);
  };

  const uploadButton = (
    <div
      className={
        large
          ? styles['large-company-upload']
          : thumbnail
          ? styles['thumbail-image']
          : icon
          ? styles['icon-image']
          : styles['company-upload']
      }>
      <img className={styles['company-image']} src={imgUpload} alt="upload" />
      <div className={styles['upload-text']}>
        <h3>{loading ? 'Uploading...' : label}</h3>
      </div>
    </div>
  );

  return (
    <div>
      {(previewUrl || uploadedImageUrl) && !loading ? (
        <div className={classNameStyle}>
          <img src={uploadedImageUrl ?? previewUrl} alt="company" />
          <Button
            label="Delete Image"
            type="secondary"
            className={styles['button-red']}
            onClick={() => {
              setImgUrl(null);
              setPreviewUrl(null);
              setUploadedImageUrl(null);
            }}
          />
        </div>
      ) : imageProgress ? (
        <div className={classNameStyle}>
          <div className={styles['image-progress']}>
            <Spin size="small" />
            <h3>Uploading... {imageProgress || 0}%</h3>
          </div>
        </div>
      ) : (
        <ImageUpload
          name="image"
          listType="picture"
          onChange={handleChange}
          showUploadList={false}
          maxCount={1}
          accept={accept}>
          {uploadButton}
        </ImageUpload>
      )}
    </div>
  );
};

export default UploadImage;
